import { createContext, useContext, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { SitzungsKarte } from "@/portal/SitzungsKarte";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Kundenkontext (Arbeitsbereich): läuft ausschließlich über das HttpOnly-Cookie `view_as` – kein Token in URL, Hash oder sessionStorage.
const isBrowser = typeof window !== "undefined";
if (isBrowser && /viewas=/.test(window.location.hash)) window.history.replaceState(null, "", window.location.pathname + "#uebersicht");  // Alt-Links entschärfen
export const viewAsToken = () => "";
export const withVa = (url) => url;  // Bild-/Download-Links: Cookie wird automatisch mitgesendet
export const clearViewAs = () => { if (isBrowser) sessionStorage.removeItem("dimedial-view-as"); };
export const kontextBeenden = async () => {
  try { await axios.delete(`${API}/auth/view-as`, { withCredentials: true }); } catch { /* Cookie ggf. schon weg */ }
  clearViewAs();
};
// FastAPI-Validierungsfehler (422) kommen als Liste – als Text normalisieren, sonst rendert toast.error ein Objekt und React stürzt ab (weiße Seite).
axios.interceptors.response.use(undefined, (err) => {
  const d = err?.response?.data;
  // Demo-Modus: serverseitiger Nur-Lesen-Schutz meldet sich mit code=demo_readonly → freundliches Hinweis-Fenster.
  if (isBrowser && err?.response?.status === 403 && d?.detail && d.detail.code === "demo_readonly") {
    window.dispatchEvent(new CustomEvent("demo-readonly", { detail: d.detail }));
    d.detail = d.detail.message;  // damit vorhandene catch-Handler (toast.error/setErr) einen String rendern, nicht das Objekt
  }
  if (d && d.detail != null && typeof d.detail !== "string" && !(!Array.isArray(d.detail) && d.detail.code)) {  // strukturierte Fehler mit `code` bleiben Objekte
    d.detail = Array.isArray(d.detail) ? d.detail.map((x) => `${(x.loc || []).slice(1).join(".")}: ${x.msg}`).join(" · ") : JSON.stringify(d.detail);
  }
  return Promise.reject(err);
});
const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(undefined);

  useEffect(() => {
    const portalArea = ["/portal", "/admin", "/kunden", "/social", "/mein-brain", "/mein-tag", "/baukasten", "/studio-editor", "/studio-guthaben", "/app", "/cloud-free", "/link-in-bio", "/abnahme"].some((p) => window.location.pathname.startsWith(p));
    if (!portalArea) {
      setUser(false);
      return;
    }
    fetch(`${API}/auth/me`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setUser(d || false))
      .catch(() => setUser(false));
  }, []);

  const logout = async () => {
    clearViewAs();  // Kundenansicht-Token nie in die nächste Sitzung mitnehmen
    try {
      await fetch(`${API}/auth/logout`, { method: "POST", credentials: "include" });
    } catch {}
    setUser(false);
  };

  return <AuthCtx.Provider value={{ user, setUser, logout }}>{children}</AuthCtx.Provider>;
};

export const ProtectedRoute = ({ role, children }) => {
  const { user } = useAuth();
  if (user === undefined) {
    return <div className="min-h-screen bg-ink" data-testid="auth-loading" />;
  }
  if (!user) return <Navigate to={(window.location.pathname !== "/portal" || window.location.hash) ? `/kunden?next=${encodeURIComponent(window.location.pathname + window.location.hash)}` : "/kunden"} replace />;
  if (role === "admin" && user.role !== "admin") return <Navigate to="/portal" replace />;
  // Admin landet nie „als Kunde“ im Kundenbereich – AUSSER im Studio-Editor: dort darf der Admin eigene
  // Projekte (und per view_as Kundenprojekte) direkt öffnen, Hinweis „Du arbeitest für Kunde X“ im Editor.
  const studioDirekt = window.location.pathname.startsWith("/studio-editor");
  if (role !== "admin" && user.role === "admin" && !user.view_as && !studioDirekt) return <SitzungsKarte user={user} wechselZiel={`/kunden?next=${encodeURIComponent(window.location.pathname + window.location.hash)}`} />;
  return children;
};

export { API };
